// Helper promise to resolve as soon as Splide assets load
window.SplideLoaded = window.SplideLoaded || new Promise((resolve) => {
  if (window.Splide) {
    resolve();
  } else {
    // Check if the script exists and listen for load, otherwise fallback to polling
    const script = document.querySelector('script[src*="splide.min.js"]');
    if (script) {
      script.addEventListener('load', () => resolve());
      script.addEventListener('readystatechange', () => {
        if (script.readyState === 'complete' || script.readyState === 'loaded') resolve();
      });
    } else {
      const interval = setInterval(() => {
        if (window.Splide) {
          clearInterval(interval);
          resolve();
        }
      }, 50);
    }
  }
});

class CollectionSection extends HTMLElement {
  connectedCallback() {
    this.initSlider();
    
    // Shopify Theme Editor Listeners for blocks
    this.onBlockSelectHandler = this.onBlockSelect.bind(this);
    this.onBlockDeselectHandler = this.onBlockDeselect.bind(this);
    document.addEventListener('shopify:block:select', this.onBlockSelectHandler);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselectHandler);

    // Shopify Theme Editor Listeners for section load/unload to guarantee smooth re-initialization
    this.onSectionLoadHandler = this.onSectionLoad.bind(this);
    this.onSectionUnloadHandler = this.onSectionUnload.bind(this);
    document.addEventListener('shopify:section:load', this.onSectionLoadHandler);
    document.addEventListener('shopify:section:unload', this.onSectionUnloadHandler);
  }

  disconnectedCallback() {
    this.destroySlider();
    document.removeEventListener('shopify:block:select', this.onBlockSelectHandler);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselectHandler);
    document.removeEventListener('shopify:section:load', this.onSectionLoadHandler);
    document.removeEventListener('shopify:section:unload', this.onSectionUnloadHandler);
  }

  onSectionLoad(event) {
    // Re-initialize slider if this section is loaded/reloaded in Theme Editor
    if (event.target.contains(this) || event.target === this) {
      this.initSlider();
    }
  }

  onSectionUnload(event) {
    // Safely destroy slider if this section is unloaded in Theme Editor
    if (event.target.contains(this) || event.target === this) {
      this.destroySlider();
    }
  }

  initSlider() {
    window.SplideLoaded.then(() => {
      this.mountSplide();
    });
  }

  mountSplide() {
    // Ensure we don't double mount
    this.destroySlider();

    const splideEl = this.querySelector('.splide');
    if (!splideEl) return;
    
    const perPageDesktop = parseInt(this.dataset.perPageDesktop) || 6;
    const autoplay = this.dataset.autoplay === 'true';
    const autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 3000;

    const splideOptions = {
    type: 'loop',
    drag: 'free',
    focus: 'center',
    arrows: false,
    pagination: false,
    perPage: perPageDesktop,
    gap: '20px', // default = above 1440px

    breakpoints: {
        1440: {
        gap: '15px', // 1440px ↓ to 1281px
        perPage: 8,
        },
        1280: {
        perPage: Math.max(2, perPageDesktop - 2),
        },
        1024: {
        perPage: Math.max(2, perPageDesktop - 3),
        gap: '12px',
        },
        990: {
        perPage: 4,
        },
        768: {
        perPage: 3.5,
        },
        576: {
        perPage: 2.5,
        }
    }
    };

    if (autoplay) {
      splideOptions.autoplay = true;
      splideOptions.interval = autoplaySpeed;
    } else {
      splideOptions.autoplay = false;
    }

    this.splide = new Splide(splideEl, splideOptions);
    this.splide.mount();
  }

  destroySlider() {
    if (this.splide) {
      this.splide.destroy();
      this.splide = null;
    }
  }

  onBlockSelect(event) {
    if (!this.splide) return;
    
    const block = event.target.closest('.splide__slide');
    if (!block || !this.contains(block)) return;

    // Find index of the slide, ignoring duplicate cloned slides
    const slides = Array.from(this.querySelectorAll('.splide__slide:not(.splide__slide--clone)'));
    const index = slides.indexOf(block);
    if (index === -1) return;

    // Pause autoplay to allow editing
    if (this.splide.Components.Autoplay) {
      this.splide.Components.Autoplay.pause();
    }

    // Instantly slide to the selected block
    this.splide.go(index);
  }

  onBlockDeselect(event) {
    if (!this.splide) return;

    const block = event.target.closest('.splide__slide');
    if (!block || !this.contains(block)) return;

    const autoplay = this.dataset.autoplay === 'true';

    // Resume autoplay if enabled
    if (autoplay && this.splide.Components.Autoplay) {
      this.splide.Components.Autoplay.play();
    }
  }
}

customElements.define('collection-section', CollectionSection);
